// Deterministic auto-fix layer for baking recipes. Takes a recipe
// plus the diagnostics from validateBakingRecipe and returns a
// repaired recipe + a list of changes applied. The goal is to NOT
// hand the user a "this is broken" warning when we already know
// the canonical correction — apply it, log it, surface the diff.
//
// Companion to src/lib/bakingRecipeValidator.js. The validator
// detects; this module corrects. Both run client-side as a
// post-pass before setRecipe lands in AIRecipe.jsx.
//
// Scope rule: apply ONLY corrections whose right answer is
// unambiguous. Step reorders, text rewrites for known protocols
// (windowpane kneading, baking soda needing acid), missing proof
// steps, knead timer bumps — all canonical fixes a chef would
// agree on. Anything ambiguous (which flour to swap, how aggressive
// to scale a wild-out-of-band ratio) gets left in the diagnostics
// for the user to decide.

// ─── Helpers ────────────────────────────────────────────────────

// Parse an amount string like "0.25 tsp" / "1.5 tbsp" into
// { numeric, unit }. Returns null if unparseable. Tolerant of
// unicode fractions ("¼ tsp") via simple replacement.
const UNICODE_FRACTIONS = {
  "¼": 0.25, "½": 0.5, "¾": 0.75,
  "⅓": 0.333, "⅔": 0.667,
  "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875,
};
function parseAmountSimple(amount) {
  if (amount == null) return null;
  let s = String(amount).trim().toLowerCase();
  if (!s) return null;
  // Replace any unicode fraction with its decimal so the regex
  // below catches it as part of the numeric prefix.
  for (const [ch, val] of Object.entries(UNICODE_FRACTIONS)) {
    if (s.includes(ch)) s = s.replace(ch, ` ${val}`);
  }
  // Match leading integer + optional fractional + optional decimal.
  // Accepts "1 0.5 tsp" (formed from "1½ tsp" via the substitution
  // above) — sums the integer + fractional.
  const m = s.match(/^\s*(\d+(?:\.\d+)?)(?:\s+(\d+(?:\.\d+)?))?\s*(.*)$/);
  if (!m) return null;
  const a = Number(m[1]);
  const b = m[2] ? Number(m[2]) : 0;
  const numeric = a + b;
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  const unit = (m[3] || "").trim().split(/\s+/)[0] || "";
  return { numeric, unit };
}

function formatAmount(numeric, unit) {
  if (!Number.isFinite(numeric)) return unit || "";
  let display;
  if (numeric < 0.125) display = numeric.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  else if (numeric < 1)  display = numeric.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  else if (numeric % 1 === 0) display = String(Math.round(numeric));
  else display = numeric.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  return unit ? `${display} ${unit}` : display;
}

// Step text reader — schema-tolerant (instruction / body / text).
function stepText(s) {
  return s?.instruction || s?.body || s?.text || "";
}

// Build a fresh step id that doesn't collide with existing ones.
// Used when we insert a new step (e.g. yeast proof) so the new
// row carries a unique key.
function freshStepId(steps, prefix) {
  const existing = new Set((steps || []).map((s) => String(s?.id || "")));
  let n = steps.length + 1;
  while (existing.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
}

// ─── Auto-fix transformations ──────────────────────────────────

// CHECK 4 fix: butter added before kneading.
// The validator told us butterStep < glutenStep. Swap their
// positions in steps[]. Step ids stay attached to their own row
// (so step1's id rides with whichever physical step1's content
// lands at the swapped position — we DO renumber so the user
// reads them in order).
function fixButterTooEarly(recipe) {
  const steps = [...(recipe.steps || [])];
  const butterIdx = steps.findIndex((s) =>
    /\b(add|incorporate|work in).*\bbutter\b/i.test(stepText(s))
  );
  const glutenIdx = steps.findIndex((s) =>
    /\b(knead|develop|window.?pane|gluten)\b/i.test(stepText(s))
  );
  if (butterIdx === -1 || glutenIdx === -1 || butterIdx >= glutenIdx) return null;
  // Swap.
  [steps[butterIdx], steps[glutenIdx]] = [steps[glutenIdx], steps[butterIdx]];
  // Renumber numeric step ids (step1 / step2 / ...) so they read
  // top-to-bottom after the swap. Non-numeric ids left alone.
  const renumbered = steps.map((s, i) => {
    if (typeof s?.id === "string" && /^step\d+$/.test(s.id)) {
      return { ...s, id: `step${i + 1}` };
    }
    return s;
  });
  return {
    recipe: { ...recipe, steps: renumbered },
    change: {
      kind: "butter_too_early",
      summary: "Moved the butter-incorporation step AFTER the kneading step so gluten develops first.",
    },
  };
}

// CHECK 4 fix (warning variant): "gluten_development_unclear".
// Find the step that mixes flour + liquid (the precursor to butter
// addition) and rewrite its instruction + timer to include
// explicit windowpane-style kneading. Conservative: appends a
// sentence rather than rewriting the whole step.
function fixGlutenDevelopmentUnclear(recipe) {
  const steps = [...(recipe.steps || [])];
  // Find the step right before the butter-add step; that's the
  // canonical "mix to shaggy" step that needs the knead extension.
  const butterIdx = steps.findIndex((s) =>
    /\b(add|incorporate|work in).*\bbutter\b/i.test(stepText(s))
  );
  if (butterIdx <= 0) return null;
  const mixIdx = butterIdx - 1;
  const step = { ...steps[mixIdx] };
  const orig = stepText(step);
  // Already mentions kneading + a long timer? Don't double-write.
  if (/\b(knead|window.?pane)\b/i.test(orig) && (Number(step.timer) || 0) >= 600) return null;
  const append = " Then knead on medium speed until the dough passes the windowpane test (8–12 minutes) — pull a small piece, it should stretch thin enough to see light through without tearing.";
  if ("instruction" in step) step.instruction = orig.trim() + append;
  else if ("body" in step)   step.body        = orig.trim() + append;
  else                       step.instruction = orig.trim() + append;
  // Bump the timer up to at least 10 min so the cook-screen
  // countdown matches the new windowpane instruction.
  step.timer = Math.max(Number(step.timer) || 0, 600);
  if (Array.isArray(step.timers) && step.timers.length > 0) {
    // Last stage gets the windowpane minimum.
    step.timers = step.timers.map((t, i) =>
      i === step.timers.length - 1
        ? { ...t, seconds: Math.max(Number(t?.seconds) || 0, 600) }
        : t
    );
  }
  steps[mixIdx] = step;
  return {
    recipe: { ...recipe, steps },
    change: {
      kind: "gluten_development_unclear",
      summary: "Extended the mixing step to 10+ min of kneading with a windowpane-test done-cue.",
    },
  };
}

// CHECK 4 sub-fix: knead step has a timer < 5 min.
// Bump to 600s. Also append windowpane language if missing.
function fixKneadTooShort(recipe) {
  const steps = [...(recipe.steps || [])];
  const glutenIdx = steps.findIndex((s) =>
    /\b(knead|window.?pane|develop|gluten)\b/i.test(stepText(s))
  );
  if (glutenIdx === -1) return null;
  const step = { ...steps[glutenIdx] };
  const sec = Number(step.timer) || 0;
  if (sec === 0 || sec >= 300) return null;
  step.timer = 600;
  if (Array.isArray(step.timers) && step.timers.length > 0) {
    step.timers = step.timers.map((t, i) =>
      i === step.timers.length - 1
        ? { ...t, seconds: Math.max(Number(t?.seconds) || 0, 600) }
        : t
    );
  }
  // Append windowpane phrasing only when it's not already there.
  const orig = stepText(step);
  if (!/window.?pane/i.test(orig)) {
    const append = " Continue until the dough passes the windowpane test — a small piece should stretch thin enough to see light through.";
    if ("instruction" in step) step.instruction = orig.trim() + append;
    else if ("body" in step)   step.body        = orig.trim() + append;
    else                       step.instruction = orig.trim() + append;
  }
  steps[glutenIdx] = step;
  return {
    recipe: { ...recipe, steps },
    change: {
      kind: "knead_too_short",
      summary: "Extended the knead timer to 10 min and added a windowpane done-cue.",
    },
  };
}

// CHECK 5 fix: yeast present but no proof / rise step.
// Insert a "First rise" step after the kneading step (or after
// the last mixing/butter-incorporation step if no knead found).
function fixYeastNoProof(recipe) {
  const steps = [...(recipe.steps || [])];
  // Anchor: after the kneading step OR after the butter-add OR
  // after the first mix step — whichever exists latest in the
  // sequence so the proof comes after dough formation is done.
  let anchorIdx = steps.findIndex((s) =>
    /\b(knead|window.?pane)\b/i.test(stepText(s))
  );
  if (anchorIdx === -1) {
    anchorIdx = steps.findIndex((s) =>
      /\b(add|incorporate|work in).*\bbutter\b/i.test(stepText(s))
    );
  }
  if (anchorIdx === -1) {
    // Find the latest dough-formation step (one with "mix" or
    // "stir" or "combine").
    for (let i = steps.length - 1; i >= 0; i -= 1) {
      if (/\b(mix|stir|combine|whisk together|create.*dough|form.*dough)\b/i.test(stepText(steps[i]))) {
        anchorIdx = i;
        break;
      }
    }
  }
  if (anchorIdx === -1) return null;
  const newStep = {
    id: freshStepId(steps, "step"),
    icon: "⏱️",
    title: "First rise",
    instruction: "Transfer the dough to a lightly oiled bowl, cover, and let rise in a warm spot (75°F) until roughly doubled in volume, about 60–90 minutes. Poke gently — the dough should spring back slowly.",
    timer: 5400,
    timers: null,
    doneCue: "Dough has roughly doubled and springs back slowly when poked",
    uses: [],
    heat: "off",
    overnight: false,
    tip: "For deeper flavor, refrigerate overnight (12–18 hours) instead of room-temperature rise.",
  };
  steps.splice(anchorIdx + 1, 0, newStep);
  // Renumber stepN ids.
  const renumbered = steps.map((s, i) => {
    if (typeof s?.id === "string" && /^step\d+$/.test(s.id)) {
      return { ...s, id: `step${i + 1}` };
    }
    return s;
  });
  return {
    recipe: { ...recipe, steps: renumbered },
    change: {
      kind: "yeast_no_proof",
      summary: "Inserted a 60–90 min first-rise step so the dough actually proofs before shaping.",
    },
  };
}

// CHECK 2 fix: baking soda used but no acid in recipe. Swap soda
// → baking powder, scale amount ×3 (standard substitution ratio).
function fixSodaNoAcid(recipe) {
  const ings = [...(recipe.ingredients || [])];
  const sodaIdx = ings.findIndex((i) => i?.ingredientId === "baking_soda");
  if (sodaIdx === -1) return null;
  const soda = { ...ings[sodaIdx] };
  const parsed = parseAmountSimple(soda.amount);
  let newAmount;
  if (parsed) {
    newAmount = formatAmount(parsed.numeric * 3, parsed.unit);
  } else {
    // Couldn't parse — keep the existing amount but flag.
    newAmount = soda.amount;
  }
  ings[sodaIdx] = {
    ...soda,
    ingredientId: "baking_powder",
    item: soda.item ? soda.item.replace(/baking\s+soda/i, "baking powder") : "baking powder",
    amount: newAmount,
  };
  // Also rewrite any step prose that names baking soda.
  const steps = (recipe.steps || []).map((s) => {
    if (!s) return s;
    const txt = stepText(s);
    if (!/baking\s+soda/i.test(txt)) return s;
    const rewrite = txt.replace(/baking\s+soda/gi, "baking powder");
    const next = { ...s };
    if ("instruction" in s) next.instruction = rewrite;
    else if ("body" in s)   next.body        = rewrite;
    else                    next.instruction = rewrite;
    return next;
  });
  return {
    recipe: { ...recipe, ingredients: ings, steps },
    change: {
      kind: "soda_no_acid",
      summary: `Swapped baking soda → baking powder (3× amount) since no acid was in the ingredient list to activate the soda.`,
    },
  };
}

// ─── Dispatcher ────────────────────────────────────────────────

// Walks the diagnostics issues + warnings and applies every
// auto-fixable correction in order. Each fix mutates a fresh
// recipe shallow-clone; subsequent fixes see the result of the
// previous one. Returns { recipe, changes } — changes is empty
// when no fixes applied.
//
// Order matters: butter_too_early runs first so subsequent
// gluten-dev / knead-timer checks act against the corrected
// step order.
const FIXERS_BY_KIND = {
  butter_too_early:             fixButterTooEarly,
  knead_too_short:              fixKneadTooShort,
  gluten_development_unclear:   fixGlutenDevelopmentUnclear,
  yeast_no_proof:               fixYeastNoProof,
  soda_no_acid:                 fixSodaNoAcid,
};

// Fix order — apply structural changes (step reorder, step insert)
// before text rewrites so the rewrites land on the corrected
// structure.
const FIX_ORDER = [
  "butter_too_early",
  "yeast_no_proof",
  "knead_too_short",
  "gluten_development_unclear",
  "soda_no_acid",
];

export function autoFixBakingRecipe(recipe, diagnostics) {
  if (!recipe || !diagnostics) return { recipe, changes: [] };
  const findings = [
    ...(Array.isArray(diagnostics.issues)   ? diagnostics.issues   : []),
    ...(Array.isArray(diagnostics.warnings) ? diagnostics.warnings : []),
  ];
  if (findings.length === 0) return { recipe, changes: [] };
  const presentKinds = new Set(findings.map((f) => f.kind));
  let current = recipe;
  const changes = [];
  for (const kind of FIX_ORDER) {
    if (!presentKinds.has(kind)) continue;
    const fixer = FIXERS_BY_KIND[kind];
    if (!fixer) continue;
    const result = fixer(current);
    if (result && result.recipe) {
      current = result.recipe;
      changes.push(result.change);
    }
  }
  return { recipe: current, changes };
}
